import React from 'react'

import { InfoIcon } from '@toasttab/buffet-pui-icons'
import classnames from 'classnames'
import { t, Trans } from 'src/translations'

import { ButtonType } from 'src/apollo/sites'

import Image from 'shared/components/common/Image'
import { BrandedButton, ButtonLink } from 'shared/components/common/button'
import Link from 'shared/components/common/link'

// eslint-disable-next-line no-restricted-imports
import { EncryptedCreditCardData } from '../online_ordering/checkout/payment/CreditCardForm'
import ReservationOverview from './ReservationOverview'
import {
  SeatingLocation,
  Deposit,
  isBookingInAdvance,
  useReservationCancellation
} from './reservationUtils'

type Props = {
  restaurantGuid: string
  reservationGuid: string
  numGuests: number
  deposit: Deposit | null
  serviceArea: SeatingLocation | null
  selectedDate: Date | null
  selectedTime: Date | null
  cancellationPolicy?: string | null
  loyaltySignupUrl?: string | null
  onCancellation: (arg0: boolean, arg1: boolean | null) => void
  timezone: string
  occasion: string | null
  creditCardData?: EncryptedCreditCardData | null
}

const ReservationConfirmation = ({
  restaurantGuid,
  reservationGuid,
  numGuests,
  deposit,
  serviceArea,
  selectedDate,
  selectedTime,
  cancellationPolicy,
  loyaltySignupUrl,
  onCancellation,
  timezone,
  occasion,
  creditCardData
}: Props) => {
  const { cancel } = useReservationCancellation(
    reservationGuid,
    restaurantGuid,
    onCancellation,
    null
  )

  return (
    <div className='confirmation' data-testid='reservation-confirmation'>
      <div className='description'>
        {t('reservation-confirmation.table-booked-sms')}
      </div>
      <div className='buttons'>
        <ButtonLink variant={ButtonType.Primary} href='/menu'>
          {t('reservation-confirmation.view-menu')}
        </ButtonLink>
        <BrandedButton
          variant={ButtonType.Secondary}
          className='secondary-btn'
          onClick={cancel}
        >
          <div>{t('reservation-confirmation.cancel-reservation')}</div>
        </BrandedButton>
        {loyaltySignupUrl && (
          <div className='loyaltyContainer'>
            <Link href={loyaltySignupUrl} isBold>
              <Image alt='Join loyalty icon' src='icons/join-loyalty.svg' />
              <div className='joinLoyalty'>
                {t('reservation-confirmation.join-loyalty-program')}
              </div>
            </Link>
          </div>
        )}
      </div>
      <ReservationOverview
        numGuests={numGuests}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        serviceArea={serviceArea}
        timezone={timezone}
        occasion={occasion}
      />
      {deposit && (
        <div className='depositSummary'>
          <h3 className='depositDescriptionTitle'>
            {t('reservation-confirmation.deposit-summary')}
          </h3>
          {deposit?.strategyType === 'ByBooking' ? (
            <div>
              <p className='depositMessage morePaddingBottom'>
                {t('reservation-confirmation.deposit-per-booking', {
                  amount: deposit?.actualAmount
                })}
              </p>
              <div className='depositPaymentContainer'>
                <p className='depositMessage black'>
                  {t('reservation-confirmation.reservation-x1')}
                </p>
                <p className='depositMessage black'>${deposit?.actualAmount}</p>
              </div>
            </div>
          ) : (
            <div>
              <p className='depositMessage morePaddingBottom'>
                {t('reservation-confirmation.deposit-per-person', {
                  amount: deposit?.amountPerGuest
                })}
              </p>
              <div className='depositPaymentContainer'>
                <p className='depositMessage black'>
                  {t('reservation-confirmation.reservation-x-guests', {
                    count: numGuests
                  })}
                </p>
                <p className='depositMessage black'>${deposit?.actualAmount}</p>
              </div>
            </div>
          )}
          <hr className='menuDividingLine menuDividingLinePaddingBottom' />
          <div className='depositPaymentContainer'>
            <p className='depositMessage morePaddingBottom black'>
              {t('reservation-confirmation.amount-paid')}
            </p>
            <p className='depositMessage morePaddingBottom black'>
              ${deposit?.actualAmount?.toFixed(2)}
            </p>
          </div>
          <p className='depositMessage bold black'>
            {t('reservation-confirmation.payment-method')}
          </p>
          <p className='depositMessage noPaddingBottom black'>
            {t('reservation-confirmation.card-ending-in', {
              last4: creditCardData?.cardLast4
            })}
          </p>
        </div>
      )}
      {deposit?.cancellationRefundableTimeframe !== 0 &&
        deposit?.cancellationRefundableTimeframe &&
        // eslint-disable-next-line no-extra-parens
        (deposit?.cancellationRefundableTimeframe === -1 ||
          // eslint-disable-next-line no-extra-parens
          (selectedDate &&
            !isBookingInAdvance(
              selectedDate,
              deposit.cancellationRefundableTimeframe
            ))) && (
          <div className='minHoursInAdvance forReservationConfirmation'>
            <InfoIcon className='infoIcon' accessibility='decorative' />
            <p className='minHoursInAdvanceMessage'>
              {t('reservation-confirmation.booking-no-longer-refundable')}
            </p>
          </div>
        )}
      <hr className='menuDividingLine menuDividingLineMarginTop' />
      {deposit?.cancellationRefundableTimeframe === 0 && (
        <div className={classnames('policies')}>
          <h3 className='cancellationTitleSection'>
            {t('reservation-confirmation.deposit-policy')}
          </h3>
          <div className='cancellationPolicy'>
            <>
              {t(
                'reservation-confirmation.deposit-refundable-until-reservation'
              )}
            </>
          </div>
        </div>
      )}
      {deposit?.cancellationRefundableTimeframe !== 0 &&
        deposit?.cancellationRefundableTimeframe &&
        deposit?.cancellationRefundableTimeframe > 0 && (
          <div className={classnames('policies')}>
            <h3 className='cancellationTitleSection'>
              {t('reservation-confirmation.deposit-policy')}
            </h3>
            <div className='cancellationPolicy'>
              <Trans
                i18nKey='reservation-confirmation.refundable-cancel-hours'
                values={{ hours: deposit.cancellationRefundableTimeframe }}
                components={[<span key='0' />]}
              />
            </div>
          </div>
        )}
      {cancellationPolicy && (
        <div className={classnames('policies')}>
          <h3 className='cancellationTitleSection'>
            {t('reservation-confirmation.reservation-policy')}
          </h3>
          <div className='cancellationPolicy'>{cancellationPolicy}</div>
        </div>
      )}
    </div>
  )
}

export default ReservationConfirmation
